"use client";

import Link from "next/link";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { cn } from "@dionatanbig2be/big2be-ui/utils";
import { MediaImage } from "@/shared/components/MediaImage";
import { Icon } from "@/shared/components/Icon";
import { ScrollArrow } from "../ScrollArrow";
import { SCROLL_TRACK_CLASS, useHorizontalScroll } from "@/shared/hooks/useHorizontalScroll";
import type { CategoryNavItem, CategoryNavVariantProps } from "../../../types/category-nav";

const ARROW_CLASS = "text-base-content/50 hover:text-base-content";

/** Ícone do card: imagem do portal > ícone conhecido > inicial do label. */
function CardMedia({ cat }: { cat: CategoryNavItem }) {
    if (cat.imageUrl) {
        return (
            <MediaImage
                path={cat.imageUrl}
                alt=""
                width={40}
                height={40}
                className="h-10 w-10 object-contain opacity-60 transition-opacity group-hover:opacity-100"
            />
        );
    }
    const initial = <span className="text-2xl font-light">{cat.label.charAt(0)}</span>;
    return <Icon name={cat.icon} size={28} fallback={initial} />;
}

/**
 * CategoryNav — variant-2: categorias com ícone/imagem acima do texto (cards),
 * scroll horizontal com setas laterais. Sem "Departamentos"/painel. Com
 * `highlightItems` (destaques do portal), os cards exibem esses itens no lugar
 * das categorias raiz.
 */
export function CategoryNavVariant2({ categories, highlightItems }: CategoryNavVariantProps) {
    // Mesmo trilho da vitrine de produtos e da variant-default: touch nativo
    // com inércia, Shift + roda, setas medidas por ResizeObserver.
    const { ref, canLeft, canRight, update, scrollBy } = useHorizontalScroll({ step: 200 });

    const tabs: CategoryNavItem[] = highlightItems?.length ? highlightItems : categories;

    if (tabs.length === 0) return null;

    return (
        <nav
            className="mb-6 w-full border-b border-base-200 bg-base-100"
            data-variant="variant2"
            aria-label="Categorias"
        >
            <div className="relative mx-auto max-w-7xl px-0 md:px-4">
                {canLeft && (
                    <ScrollArrow
                        side="left"
                        onClick={() => scrollBy("left")}
                        className={ARROW_CLASS}
                    >
                        <RiArrowLeftSLine size={20} />
                    </ScrollArrow>
                )}

                <div
                    ref={ref}
                    onScroll={update}
                    className={SCROLL_TRACK_CLASS}
                    // Mobile o trilho sangra até a borda (container `px-0`): o
                    // recuo vai no conteúdo, e o scroll-padding alinha o 1º
                    // item ao mesmo recuo ao voltar até o início.
                    style={{ scrollPaddingInline: "var(--page-gutter)" }}
                >
                    <ul className="flex items-start gap-6 py-4 px-[var(--page-gutter)] md:px-0">
                        {tabs.map((cat) => (
                            <li key={cat.id} className="shrink-0">
                                <Link
                                    href={cat.href ?? "#"}
                                    className="group flex w-20 flex-col items-center gap-2"
                                >
                                    <div
                                        className={cn(
                                            "flex h-12 w-12 items-center justify-center transition-colors group-hover:text-primary",
                                            cat.featured
                                                ? "rounded-2xl text-primary [--nav-featured-color:var(--color-primary)] animate-nav-featured-blink motion-reduce:animate-none motion-reduce:bg-primary/10"
                                                : "text-primary/60"
                                        )}
                                    >
                                        <CardMedia cat={cat} />
                                    </div>
                                    <span className="text-center text-[11px] font-semibold uppercase leading-tight tracking-wide text-base-content/60 transition-colors group-hover:text-primary">
                                        {cat.label}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {canRight && (
                    <ScrollArrow
                        side="right"
                        onClick={() => scrollBy("right")}
                        className={ARROW_CLASS}
                    >
                        <RiArrowRightSLine size={20} />
                    </ScrollArrow>
                )}
            </div>
        </nav>
    );
}
